import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { DollarSign, Calendar, Upload, X, CircleCheck as CheckCircle2, TriangleAlert as AlertTriangle, Eye, Copy, FileText, Search, RefreshCw } from 'lucide-react';
import DatePicker from 'react-datepicker';
import InputMask from 'react-input-mask-next';
import "react-datepicker/dist/react-datepicker.css";

interface Client {
  id: string;
  client_name: string;
  cpf: string;
  phone: string;
  relative_phones: { numero: string; relacao: string }[];
  email: string;
  address: string;
  business_address: string;
  pix_key: string;
  code: string;
  outstanding_balance?: number;
  seller: {
    email: string;
  } | null;
  contract_files: {
    id: string;
    file_name: string;
    file_url: string;
    file_type: string;
  }[];
  contracts: {
    id: string;
    loan_amount: number;
    total_amount: number;
    created_at: string;
    payment_status: string;
    ruta: number;
    installments_data: {
      id: string;
      due_date: string;
      amount: number;
      status: string;
      paid_amount?: number;
    }[];
  }[];
  overall_status: 'active' | 'inactive';
  installments: {
    id: string;
    due_date: string;
    amount: number;
    status: string;
    paid_amount?: number;
    contract_id: string;
  }[];
  displayContract: {
    id: string;
    loan_amount: number;
    total_amount: number;
    created_at: string;
    payment_status: string;
    ruta: number;
    outstanding_balance?: number;
    installments_data: {
      id: string;
      due_date: string;
      amount: number;
      status: string;
      paid_amount?: number;
    }[];
  };
}

interface PaymentData {
  installmentId: string;
  paidAmount: string;
  lateFee: string;
  paymentDateTime: Date;
  bankName: string;
  proofFiles: File[];
}

export default function Collections() {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);
  const [nameSearchTerm, setNameSearchTerm] = useState('');
  const [cpfSearchTerm, setCpfSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedInstallment, setSelectedInstallment] = useState<any>(null);
  const [submittingPayment, setSubmittingPayment] = useState(false);
  const [copied, setCopied] = useState(false);
  const [syncingContractId, setSyncingContractId] = useState<string | null>(null);
  const { user } = useAuth();

  const [paymentData, setPaymentData] = useState<PaymentData>({
    installmentId: '',
    paidAmount: '',
    lateFee: '0',
    paymentDateTime: new Date(),
    bankName: '',
    proofFiles: []
  });

  const fetchClients = async () => {
    if (!user) return;
    
    // Validate that at least one search term is provided
    if (!nameSearchTerm.trim() && !cpfSearchTerm.trim()) {
      setError('Por favor, digite pelo menos um filtro (nome ou CPF) para buscar clientes.');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      let query = supabase
        .from('contracts')
        .select(`
          id,
          client_name,
          cpf,
          phone,
          relative_phones,
          email,
          address,
          business_address,
          pix_key,
          code,
          outstanding_balance,
          seller:seller_id(email),
          contract_files(*),
          loan_amount,
          total_amount,
          created_at,
          payment_status,
          ruta,
          installments_data:installments(*)
        `)
        .eq('unit_id', user?.unit_id)
        .eq('approval_status', 'approved')
        .order('created_at', { ascending: false });

      // Apply filters
      if (nameSearchTerm.trim()) {
        query = query.ilike('client_name', `%${nameSearchTerm.trim()}%`);
      }
      
      if (cpfSearchTerm.trim()) {
        const cleanCpf = cpfSearchTerm.replace(/\D/g, '');
        query = query.ilike('cpf', `%${cleanCpf}%`);
      }

      const { data: contracts, error: contractsError } = await query;

      if (contractsError) throw contractsError;

      // Group contracts by client (using CPF as unique identifier)
      const clientsMap = new Map<string, Client>();
      
      contracts?.forEach(contract => {
        const existingClient = clientsMap.get(contract.cpf);
        
        if (existingClient) {
          existingClient.contracts.push({
            id: contract.id,
            loan_amount: contract.loan_amount,
            total_amount: contract.total_amount,
            created_at: contract.created_at,
            payment_status: contract.payment_status,
            ruta: contract.ruta,
            installments_data: contract.installments_data
          });
          
          // Add contract files from this contract to the existing client
          if (contract.contract_files && contract.contract_files.length > 0) {
            existingClient.contract_files.push(...contract.contract_files);
          }

          // Add installments from this contract
          if (contract.installments_data && contract.installments_data.length > 0) {
            contract.installments_data.forEach(installment => {
              existingClient.installments.push({
                ...installment,
                contract_id: contract.id
              });
            });
          }

          // Update outstanding balance to the latest contract's value
          existingClient.outstanding_balance = contract.outstanding_balance;
        } else {
          clientsMap.set(contract.cpf, {
            id: contract.id,
            client_name: contract.client_name,
            cpf: contract.cpf,
            phone: contract.phone,
            relative_phones: contract.relative_phones,
            email: contract.email,
            address: contract.address,
            business_address: contract.business_address,
            pix_key: contract.pix_key,
            code: contract.code,
            outstanding_balance: contract.outstanding_balance,
            seller: contract.seller,
            contract_files: contract.contract_files || [],
            overall_status: 'inactive', // Default to inactive, will be updated
            displayContract: {
              id: contract.id,
              loan_amount: contract.loan_amount,
              total_amount: contract.total_amount,
              created_at: contract.created_at,
              payment_status: contract.payment_status,
              ruta: contract.ruta,
              outstanding_balance: contract.outstanding_balance,
              installments_data: contract.installments_data
            },
            installments: contract.installments_data ? contract.installments_data.map(installment => ({
              ...installment,
              contract_id: contract.id
            })) : [],
            contracts: [{
              id: contract.id,
              loan_amount: contract.loan_amount,
              total_amount: contract.total_amount,
              created_at: contract.created_at,
              payment_status: contract.payment_status,
              ruta: contract.ruta,
              installments_data: contract.installments_data
            }]
          });
        }
      });

      // Deduplicate contract files for each client
      clientsMap.forEach((client) => {
        const uniqueFiles = new Map();
        client.contract_files.forEach(file => {
          uniqueFiles.set(file.id, file);
        });
        client.contract_files = Array.from(uniqueFiles.values());
      });

      // Post-processing to determine overall_status and displayContract for each client
      clientsMap.forEach((client, cpf) => {
        // Determine the current contract (most recent with pending installments, or most recent overall)
        let currentContract = null;
        
        // First, try to find the most recent contract with pending installments
        for (const contract of client.contracts) {
          const hasPendingInstallments = contract.installments_data.some(
            installment => installment.status !== 'paid'
          );
          if (hasPendingInstallments) {
            currentContract = contract;
            break; // Since contracts are already sorted by created_at desc, this is the most recent with pending
          }
        }
        
        // If no contract with pending installments, use the most recent contract
        if (!currentContract && client.contracts.length > 0) {
          currentContract = client.contracts[0]; // Most recent contract
        }
        
        // Update displayContract with the current contract
        if (currentContract) {
         // Calculate outstanding balance dynamically from pending installments
         const calculatedOutstandingBalance = currentContract.installments_data.reduce((sum, installment) => {
           if (installment.status !== 'paid') {
             const remainingAmount = installment.amount - (installment.paid_amount || 0);
             return sum + Math.max(0, remainingAmount);
           }
           return sum;
         }, 0);

          client.displayContract = {
            id: currentContract.id,
            loan_amount: currentContract.loan_amount,
            total_amount: currentContract.total_amount,
            created_at: currentContract.created_at,
            payment_status: currentContract.payment_status,
            ruta: currentContract.ruta,
           outstanding_balance: calculatedOutstandingBalance, // Use calculated balance from pending installments
            installments_data: currentContract.installments_data
          };
        }
        
        const hasAnyPending = client.installments.some(
          installment => installment.status !== 'paid'
        );
        client.overall_status = hasAnyPending ? 'active' : 'inactive';
      });

      const clientsList = Array.from(clientsMap.values());
      setClients(clientsList);
      setSearched(true);
    } catch (err) {
      console.error('Error fetching clients:', err);
      setError('Erro ao carregar clientes');
    } finally {
      setLoading(false);
    }
  };

  const uploadProofFiles = async (files: File[], installmentId: string): Promise<string[]> => {
    const uploadPromises = files.map(async (file) => {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `payment-proofs/${installmentId}/${fileName}`;

      try {
        const { data: signedUrlData, error: signedUrlError } = await supabase
          .storage
          .from('contract-files')
          .createSignedUploadUrl(filePath);

        if (signedUrlError || !signedUrlData?.signedUrl) {
          throw signedUrlError || new Error('Failed to get signed upload URL');
        }

        const response = await fetch(signedUrlData.signedUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': file.type
          },
          body: file
        });

        if (!response.ok) {
          throw new Error(`Upload failed: ${response.statusText}`);
        }

        const { data: publicUrlData } = supabase.storage
          .from('contract-files')
          .getPublicUrl(filePath);

        return publicUrlData.publicUrl;
      } catch (err) {
        console.error('Error uploading file:', file.name, err);
        throw err;
      }
    });

    return Promise.all(uploadPromises);
  };

  const handleRegisterPayment = async () => {
    if (!selectedInstallment || !paymentData.paidAmount) return;

    setSubmittingPayment(true);
    try {
      const paidAmount = parseFloat(paymentData.paidAmount);
      const lateFee = parseFloat(paymentData.lateFee) || 0;
      const totalPaid = paidAmount + lateFee;

      // Upload proof files if any
      let proofFileUrls: string[] = [];
      if (paymentData.proofFiles.length > 0) {
        proofFileUrls = await uploadProofFiles(paymentData.proofFiles, selectedInstallment.id);
      }

      // 1. Update the installment with payment information
      const { error: installmentUpdateError } = await supabase
        .from('installments')
        .update({
          status: paidAmount >= selectedInstallment.amount ? 'paid' : 'partial',
          paid_amount: paidAmount,
          late_fee: lateFee,
          paid_at: new Date().toISOString(),
          payment_date_time: paymentData.paymentDateTime.toISOString(),
          bank_name: paymentData.bankName,
          proof_file_url: proofFileUrls.length > 0 ? proofFileUrls : null
        })
        .eq('id', selectedInstallment.id);

      if (installmentUpdateError) throw installmentUpdateError;

      // 2. Get current outstanding_balance from the contract
      const { data: contractBalance, error: fetchContractError } = await supabase
        .from('contracts')
        .select('outstanding_balance')
        .eq('id', selectedInstallment.contract_id)
        .single();

      if (fetchContractError) throw fetchContractError;

      // 3. Calculate new outstanding_balance
      const currentOutstandingBalance = parseFloat(contractBalance.outstanding_balance?.toString() || '0');
      const newOutstandingBalance = Math.max(0, currentOutstandingBalance - paidAmount);

      // 4. Update the outstanding_balance in contracts table
      const { error: contractUpdateError } = await supabase
        .from('contracts')
        .update({ outstanding_balance: newOutstandingBalance })
        .eq('id', selectedInstallment.contract.id);

      if (contractUpdateError) throw contractUpdateError;

      // 5. Record cash movement for the payment received
      const { error: cashMovementError } = await supabase
        .from('cash_movements')
        .insert({
          kind: 'entry',
          amount: totalPaid, // Positive value for cash inflow
          description: `Pagamento recebido - ${selectedClient?.client_name}`,
          contract_id: selectedInstallment.contract.id,
          user_id: user?.id,
          unit_id: user?.unit_id,
          created_at: paymentData.paymentDateTime.toISOString()
        });

      if (cashMovementError) {
        console.error('Error recording cash movement:', cashMovementError);
        // Don't throw error here to avoid blocking payment registration
      }

      console.log(`✅ Pagamento registrado com sucesso!`);
      console.log(`   Parcela: ${selectedInstallment.id}`);
      console.log(`   Valor pago: R$ ${paidAmount.toFixed(2)}`);
      console.log(`   Saldo devedor anterior: R$ ${currentOutstandingBalance.toFixed(2)}`);
      console.log(`   Novo saldo devedor: R$ ${newOutstandingBalance.toFixed(2)}`);
      console.log(`   Contrato: ${selectedInstallment.contract.id}`);

      // Reset form and close modal
      setShowPaymentModal(false);
      setSelectedInstallment(null);
      setSelectedClient(null);
      setPaymentData({
        installmentId: '',
        paidAmount: '',
        lateFee: '0',
        paymentDateTime: new Date(),
        bankName: '',
        proofFiles: []
      });

      // Refresh clients list
      await fetchClients();

    } catch (err) {
      console.error('Error registering payment:', err);
      setError('Erro ao registrar pagamento');
    } finally {
      setSubmittingPayment(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setPaymentData(prev => ({
      ...prev,
      proofFiles: [...prev.proofFiles, ...files]
    }));
  };

  const removeFile = (index: number) => {
    setPaymentData(prev => ({
      ...prev,
      proofFiles: prev.proofFiles.filter((_, i) => i !== index)
    }));
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchClients();
  };

  const handleCopyClientData = (client: Client) => {
    const clientData = `
Dados do Cliente:
Nome: ${client.client_name}
CPF: ${client.cpf}
Telefone: ${client.phone}

Telefones dos Parentes:
${client.relative_phones.map(phone => `${phone.relacao}: ${phone.numero}`).join('\n')}

Endereço Residencial: ${client.address}
Endereço Comercial: ${client.business_address}

${client.contracts.map(contract => `
Valor do Empréstimo: ${formatCurrency(contract.loan_amount)}
Valor Total: ${formatCurrency(contract.total_amount)}
Data: ${new Date(contract.created_at).toLocaleDateString('pt-BR')}
Ruta: ${contract.ruta}`).join('\n')}`.trim();

    navigator.clipboard.writeText(clientData);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSyncClientBalance = async (contractId: string, calculatedBalance: number) => {
    setSyncingContractId(contractId);
    try {
      const { error } = await supabase
        .from('contracts')
        .update({ outstanding_balance: calculatedBalance })
        .eq('id', contractId);

      if (error) throw error;

      // Refresh clients list to reflect the updated balance
      await fetchClients();
    } catch (err) {
      console.error('Error syncing client balance:', err);
      setError('Erro ao sincronizar saldo devedor');
    } finally {
      setSyncingContractId(null);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatCPF = (cpf: string) => {
    const numbers = cpf.replace(/\D/g, '');
    return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const formatPhone = (phone: string) => {
    const numbers = phone.replace(/\D/g, '');
    return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 lg:p-0">
      <div className="max-w-7xl mx-auto space-y-4 lg:space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-xl lg:text-2xl font-bold text-gray-800">Cobrança</h1>
        </div>

        {/* Search Filters */}
        <div className="bg-white rounded-lg shadow-md p-4 lg:p-6">
          <div className="flex items-center gap-2 mb-4">
            <Search className="w-5 h-5 text-blue-600" />
            <h2 className="text-base lg:text-lg font-semibold text-gray-800">Buscar Clientes para Cobrança</h2>
          </div>
          
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome do Cliente
                </label>
                <input
                  type="text"
                  value={nameSearchTerm}
                  onChange={(e) => setNameSearchTerm(e.target.value)}
                  placeholder="Digite o nome do cliente..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm lg:text-base"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CPF
                </label>
                <InputMask
                  mask="999.999.999-99"
                  value={cpfSearchTerm}
                  onChange={(e) => setCpfSearchTerm(e.target.value)}
                  placeholder="000.000.000-00"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm lg:text-base"
                />
              </div>
            </div>

            <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-4">
              <button
                type="submit"
                disabled={loading || (!nameSearchTerm.trim() && !cpfSearchTerm.trim())}
                className={`flex items-center justify-center gap-2 px-6 py-2 rounded-md text-white transition-colors text-sm lg:text-base ${
                  loading || (!nameSearchTerm.trim() && !cpfSearchTerm.trim())
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Buscando...</span>
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4" />
                    <span>Buscar Clientes</span>
                  </>
                )}
              </button>
              
              {(nameSearchTerm.trim() || cpfSearchTerm.trim()) && (
                <button
                  type="button"
                  onClick={() => {
                    setNameSearchTerm('');
                    setCpfSearchTerm('');
                    setClients([]);
                    setSearched(false);
                    setError(null);
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors text-sm lg:text-base"
                >
                  Limpar Filtros
                </button>
              )}
            </div>
          </form>

          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-red-700">{error}</p>
            </div>
          )}
        </div>

        {/* Results Section */}
        <div className="bg-white rounded-lg shadow-md p-4 lg:p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Buscando clientes...</span>
            </div>
          ) : !searched ? (
            <div className="text-center py-12">
              <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-base lg:text-lg font-medium text-gray-900 mb-2">Busque por clientes</h3>
              <p className="text-gray-500">
                Use os filtros acima para encontrar clientes pelo nome ou CPF.
                <br />
                Digite pelo menos um dos campos para iniciar a busca.
              </p>
            </div>
          ) : clients.length === 0 ? (
            <div className="text-center py-12">
              <AlertTriangle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-base lg:text-lg font-medium text-gray-900 mb-2">Nenhum cliente encontrado</h3>
              <p className="text-gray-500">
                Não foram encontrados clientes com os filtros aplicados.
                <br />
                Tente ajustar os termos de busca.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-base lg:text-lg font-semibold text-gray-800">
                  Resultados da Busca ({clients.length} cliente{clients.length !== 1 ? 's' : ''})
                </h2>
              </div>

              <div className="space-y-4">
                {clients.map((client) => (
                  <div key={client.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-blue-600 font-bold text-lg">
                            {client.client_name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            {client.code} {client.client_name}
                          </h3>
                          <p className="text-sm text-gray-600">{formatPhone(client.phone)}</p>
                          <p className="text-xs text-gray-500">
                            Ruta {client.displayContract?.ruta} — {client.seller?.email || 'Sem vendedor'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          client.overall_status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {client.overall_status === 'active' ? 'Ativo' : 'Inativo'}
                        </span>
                        <button
                          onClick={() => handleCopyClientData(client)}
                          className="text-blue-600 hover:text-blue-800"
                          title="Copiar dados do cliente"
                        >
                          {copied ? (
                            <CheckCircle2 className="w-5 h-5" />
                          ) : (
                            <Copy className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600">Vr. Parcela</p>
                        <p className="font-bold text-lg">
                          {client.displayContract.installments_data.length > 0 ? formatCurrency(client.displayContract.installments_data[0].amount) : 'R$ 0'}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600">Parcela No.</p>
                        <p className="font-bold text-lg">
                          {client.displayContract.installments_data.filter(i => i.status === 'paid').length + 1} / {client.displayContract.installments_data.length}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600">Pagamento</p>
                        <p className="font-bold text-lg text-green-600">
                          {formatCurrency(client.displayContract.installments_data.reduce((sum, i) => sum + (i.paid_amount || 0), 0))}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600">Saldo devedor</p>
                        <p className={`font-bold text-lg ${
                          Number(client.displayContract.outstanding_balance) === 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {formatCurrency(client.displayContract.outstanding_balance || 0)}
                        </p>
                      </div>
                    </div>

                    <div className="mt-4 flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-5 h-5 text-gray-500" />
                        <span className="text-sm text-gray-600">
                          {client.displayContract.installments_data.filter(i => i.status !== 'paid').length} parcela(s) pendente(s)
                        </span>
                        <X className="w-4 h-4 text-gray-400" />
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleSyncClientBalance(client.displayContract.id, client.displayContract.outstanding_balance || 0)}
                          disabled={syncingContractId === client.displayContract.id}
                          className={`flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm ${
                            syncingContractId === client.displayContract.id ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        >
                          <RefreshCw className={`w-4 h-4 ${syncingContractId === client.displayContract.id ? 'animate-spin' : ''}`} />
                          Sincronizar Cliente
                        </button>
                        
                        {client.displayContract.installments_data.some(i => i.status !== 'paid') && (
                          <button
                            onClick={() => {
                              const nextInstallment = client.displayContract.installments_data.find(i => i.status !== 'paid');
                              if (nextInstallment) {
                                setSelectedClient(client);
                                setSelectedInstallment({
                                  ...nextInstallment,
                                  contract: {
                                    id: client.displayContract.id,
                                    client_name: client.client_name
                                  }
                                });
                                setPaymentData(prev => ({
                                  ...prev,
                                  installmentId: nextInstallment.id,
                                  paidAmount: nextInstallment.amount.toString()
                                }));
                                setShowPaymentModal(true);
                              }
                            }}
                            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm"
                          >
                            <DollarSign className="w-4 h-4" />
                            Registrar Pagamento
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Payment Registration Modal */}
        {showPaymentModal && selectedInstallment && selectedClient && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-lg p-4 lg:p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg lg:text-xl font-bold text-gray-800">
                  Registrar Pagamento
                </h2>
                <button
                  onClick={() => {
                    setShowPaymentModal(false);
                    setSelectedInstallment(null);
                    setSelectedClient(null);
                    setPaymentData({
                      installmentId: '',
                      paidAmount: '',
                      lateFee: '0',
                      paymentDateTime: new Date(),
                      bankName: '',
                      proofFiles: []
                    });
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Client and Installment Info */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-base font-semibold text-gray-800 mb-2">Informações da Parcela</h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Cliente:</p>
                    <p className="font-medium">{selectedClient.client_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">CPF:</p>
                    <p className="font-medium">{formatCPF(selectedClient.cpf)}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Valor da Parcela:</p>
                    <p className="font-medium">{formatCurrency(selectedInstallment.amount)}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Vencimento:</p>
                    <p className="font-medium">
                      {new Date(selectedInstallment.due_date + 'T00:00:00Z').toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600">Saldo Devedor Atual:</p>
                    <p className={`font-medium ${
                      Number(selectedClient.displayContract.outstanding_balance) === 0 
                        ? 'text-green-600' 
                        : 'text-red-600'
                    }`}>
                      {formatCurrency(selectedClient.displayContract.outstanding_balance || 0)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Payment Form */}
              <div className="space-y-4">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Valor Pago (R$) *
                    </label>
                    <input
                      type="number"
                      value={paymentData.paidAmount}
                      onChange={(e) => setPaymentData(prev => ({ ...prev, paidAmount: e.target.value }))}
                      step="0.01"
                      min="0"
                      max={selectedInstallment.amount}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Juros de Atraso (R$)
                    </label>
                    <input
                      type="number"
                      value={paymentData.lateFee}
                      onChange={(e) => setPaymentData(prev => ({ ...prev, lateFee: e.target.value }))}
                      step="0.01"
                      min="0"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Data e Hora do Pagamento *
                    </label>
                    <DatePicker
                      selected={paymentData.paymentDateTime}
                      onChange={(date) => setPaymentData(prev => ({ ...prev, paymentDateTime: date || new Date() }))}
                      showTimeSelect
                      dateFormat="dd/MM/yyyy HH:mm"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Banco *
                    </label>
                    <input
                      type="text"
                      value={paymentData.bankName}
                      onChange={(e) => setPaymentData(prev => ({ ...prev, bankName: e.target.value }))}
                      placeholder="Nome do banco"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                </div>

                {/* File Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Comprovante de Pagamento
                  </label>
                  <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                    <div className="space-y-1 text-center">
                      <Upload className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="flex text-sm text-gray-600">
                        <label
                          htmlFor="proof-upload"
                          className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500"
                        >
                          <span>Faça upload do comprovante</span>
                          <input
                            id="proof-upload"
                            name="proof-upload"
                            type="file"
                            className="sr-only"
                            multiple
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={handleFileChange}
                          />
                        </label>
                        <p className="pl-1">ou arraste e solte</p>
                      </div>
                      <p className="text-xs text-gray-500">PNG, JPG, PDF até 10MB</p>
                    </div>
                  </div>

                  {paymentData.proofFiles.length > 0 && (
                    <div className="mt-4 space-y-2">
                      {paymentData.proofFiles.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-2 bg-gray-50 rounded-md"
                        >
                          <div className="flex items-center space-x-2">
                            <FileText className="w-5 h-5 text-gray-500" />
                            <span className="text-sm text-gray-700">{file.name}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Payment Impact Preview */}
                {paymentData.paidAmount && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="text-sm font-medium text-blue-800 mb-2">Impacto do Pagamento</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-blue-700">Saldo Devedor Atual:</span>
                        <span className="font-medium text-blue-900">
                          {formatCurrency(selectedClient.outstanding_balance || 0)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-blue-700">Valor a ser Pago:</span>
                        <span className="font-medium text-blue-900">
                          -{formatCurrency(parseFloat(paymentData.paidAmount) || 0)}
                        </span>
                      </div>
                      <div className="flex justify-between border-t border-blue-300 pt-1">
                        <span className="text-blue-700 font-medium">Novo Saldo Devedor:</span>
                        <span className="font-bold text-blue-900">
                          {formatCurrency(Math.max(0, (selectedClient.displayContract.outstanding_balance || 0) - (parseFloat(paymentData.paidAmount) || 0)))}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowPaymentModal(false);
                    setSelectedInstallment(null);
                    setSelectedClient(null);
                    setPaymentData({
                      installmentId: '',
                      paidAmount: '',
                      lateFee: '0',
                      paymentDateTime: new Date(),
                      bankName: '',
                      proofFiles: []
                    });
                  }}
                  className="px-4 py-2 text-gray-700 hover:text-gray-900"
                  disabled={submittingPayment}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleRegisterPayment}
                  disabled={!paymentData.paidAmount || !paymentData.bankName || submittingPayment}
                  className={`px-4 py-2 rounded-md text-white transition-colors ${
                    !paymentData.paidAmount || !paymentData.bankName || submittingPayment
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  {submittingPayment ? 'Registrando...' : 'Registrar Pagamento'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}